import { useEffect, useState } from 'react';
import axios from 'axios';
import { applyAppBadge } from '../utils/webPush';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Shared unread bell-inbox count. Single source of truth
 * (GET /api/inbox/unread-count) so every chip in the app shows the same
 * number. Polls every 30 s, refreshes on focus/visibility/storage and on the
 * app-wide 'inbox:refresh' event, and keeps the PWA icon badge in sync.
 */
export function useUnreadCount() {
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const getSubscriptionId = () => {
      try {
        return localStorage.getItem('find2day_sub_id') || localStorage.getItem('onesignal_sub_id') || localStorage.getItem('subscriptionId') || '';
      } catch { return ''; }
    };
    const getAuthToken = () => {
      try {
        return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      } catch { return ''; }
    };

    const fetchUnread = async () => {
      const subId = getSubscriptionId();
      const token = getAuthToken();
      if (!subId && !token) { if (!cancelled) setUnread(0); return; }
      try {
        const params = subId ? `?subscriptionId=${encodeURIComponent(subId)}` : '';
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/inbox/unread-count${params}`, { headers });
        const n = res.data?.unread || 0;
        if (cancelled) return;
        setUnread(n);
        // Keep the PWA app-icon badge in sync from the foreground (iOS/desktop;
        // Android has no numeric badge — this in-app chip is its replacement).
        applyAppBadge(n);
      } catch { /* silent — never throws */ }
    };

    const tick = () => { if (!cancelled) fetchUnread(); };
    tick();
    const id = setInterval(tick, 30000);
    const onFocus = () => tick();
    const onVisibility = () => { if (document.visibilityState === 'visible') tick(); };
    const onStorage = (e) => {
      if (!e || e.key === null || e.key === 'token' || e.key === 'onesignal_sub_id' || e.key === 'subscriptionId') tick();
    };
    const onCustom = () => tick();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);
    window.addEventListener('inbox:refresh', onCustom);

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('inbox:refresh', onCustom);
    };
  }, []);

  return unread;
}

export default useUnreadCount;
